import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { 
  Zap, 
  ArrowLeft, 
  Search, 
  Building2, 
  MapPin, 
  Briefcase,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuditPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    businessName: "",
    serviceType: "",
    customService: "",
    location: "",
    websiteUrl: "",
    hasGbp: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("");

  const loadingMessages = [
    "Initializing AI analysis...",
    "Querying major language models...",
    "Checking Google search rankings...",
    "Analyzing competitor visibility...",
    "Gathering review data...",
    "Identifying market gaps...",
    "Compiling recommendations...",
    "Generating your report..."
  ];

  const runAudit = async () => {
    const serviceToUse = formData.serviceType === "other" ? formData.customService : formData.serviceType;
    
    if (!formData.businessName || !formData.serviceType || !formData.location) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (formData.serviceType === "other" && !formData.customService) {
      toast.error("Please enter your service type");
      return;
    }

    setIsLoading(true);
    setLoadingProgress(0);

    // Simulate progress updates
    let messageIndex = 0;
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        const next = prev + Math.random() * 15;
        return next > 90 ? 90 : next;
      });
      setLoadingMessage(loadingMessages[messageIndex % loadingMessages.length]);
      messageIndex++;
    }, 800);

    try {
      const response = await axios.post(`${API}/audit`, {
        business_name: formData.businessName,
        service_type: serviceToUse,
        location: formData.location,
        website_url: formData.websiteUrl || null,
        has_gbp: formData.hasGbp
      });

      clearInterval(progressInterval);
      
      // Check if we got a valid response with an ID
      if (response.data && response.data.id) {
        setLoadingProgress(100);
        setLoadingMessage("Analysis complete!");
        setTimeout(() => {
          navigate(`/report/${response.data.id}`);
        }, 500);
      } else {
        console.error("Invalid response:", response.data);
        toast.error("Failed to run audit. Please try again.");
        setIsLoading(false);
      }
    } catch (error) {
      clearInterval(progressInterval);
      console.error("Audit failed:", error);
      toast.error("Failed to run audit. Please try again.");
      setIsLoading(false);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-slate-600 hover:text-[#0a2540] transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0a2540] flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#d4af37]" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#0a2540]">PromptReady</span>
            </div>
            <div className="w-20" /> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12 md:py-20">
        {!isLoading ? (
          <div className="animate-fade-in-up">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0a2540] text-white rounded-full text-sm font-medium mb-4">
                <Search className="w-4 h-4" />
                AI Visibility Audit
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0a2540]">
                Check Your AI Visibility
              </h1>
              <p className="mt-3 text-lg text-slate-600">
                Enter your business details to see how AI assistants respond to local service queries.
              </p>
            </div>

            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)]">
              <CardHeader className="border-b border-slate-100 pb-6">
                <CardTitle className="text-lg font-semibold text-[#0a2540]">
                  Business Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="businessName" className="flex items-center gap-2 text-sm font-medium text-[#0a2540]">
                    <Building2 className="w-4 h-4" />
                    Business Name
                  </Label>
                  <Input
                    id="businessName"
                    placeholder="e.g., Smith's Roofing Ltd"
                    value={formData.businessName}
                    onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    className="h-14 rounded-lg border-slate-200 focus:border-[#0a2540] focus:ring-[#0a2540] text-lg px-4"
                    data-testid="business-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="serviceType" className="flex items-center gap-2 text-sm font-medium text-[#0a2540]">
                    <Briefcase className="w-4 h-4" />
                    Service Type
                  </Label>
                  <Select
                    value={formData.serviceType}
                    onValueChange={(value) => setFormData({ ...formData, serviceType: value })}
                  >
                    <SelectTrigger 
                      className="h-14 rounded-lg border-slate-200 focus:border-[#0a2540] focus:ring-[#0a2540] text-lg px-4"
                      data-testid="service-type-select"
                    >
                      <SelectValue placeholder="Select your service type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="roofing">Roofing / Roof Replacement</SelectItem>
                      <SelectItem value="solar">Solar Installation</SelectItem>
                      <SelectItem value="water-damage">Water Damage Restoration</SelectItem>
                      <SelectItem value="hvac">HVAC / Air Conditioning</SelectItem>
                      <SelectItem value="plumbing">Plumbing Services</SelectItem>
                      <SelectItem value="electrical">Electrical Services</SelectItem>
                      <SelectItem value="landscaping">Landscaping</SelectItem>
                      <SelectItem value="remodeling">Home Remodeling</SelectItem>
                      <SelectItem value="pest-control">Pest Control</SelectItem>
                      <SelectItem value="cleaning">Cleaning Services</SelectItem>
                      <SelectItem value="moving">Moving Services</SelectItem>
                      <SelectItem value="legal">Legal Services</SelectItem>
                      <SelectItem value="accounting">Accounting / Tax Services</SelectItem>
                      <SelectItem value="dental">Dental Services</SelectItem>
                      <SelectItem value="veterinary">Veterinary Services</SelectItem>
                      <SelectItem value="other">Other Service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.serviceType === "other" && (
                  <div className="space-y-2">
                    <Label htmlFor="customService" className="flex items-center gap-2 text-sm font-medium text-[#0a2540]">
                      <Briefcase className="w-4 h-4" />
                      Your Service Type
                    </Label>
                    <Input
                      id="customService"
                      placeholder="e.g., Pool Cleaning, Auto Detailing, Photography"
                      value={formData.customService}
                      onChange={(e) => setFormData({ ...formData, customService: e.target.value })}
                      className="h-14 rounded-lg border-slate-200 focus:border-[#0a2540] focus:ring-[#0a2540] text-lg px-4"
                      data-testid="custom-service-input"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="location" className="flex items-center gap-2 text-sm font-medium text-[#0a2540]">
                    <MapPin className="w-4 h-4" />
                    Location / Service Area
                  </Label>
                  <Input
                    id="location"
                    placeholder="e.g., Manchester, UK"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="h-14 rounded-lg border-slate-200 focus:border-[#0a2540] focus:ring-[#0a2540] text-lg px-4"
                    data-testid="location-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="websiteUrl" className="flex items-center gap-2 text-sm font-medium text-[#0a2540]">
                    <Search className="w-4 h-4" />
                    Your Website Address <span className="text-slate-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="websiteUrl"
                    placeholder="e.g., www.mybusiness.com"
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                    className="h-14 rounded-lg border-slate-200 focus:border-[#0a2540] focus:ring-[#0a2540] text-lg px-4"
                    data-testid="website-url-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-medium text-[#0a2540]">
                    <Building2 className="w-4 h-4" />
                    Do you have a Google Business Profile?
                  </Label>
                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant={formData.hasGbp === true ? "default" : "outline"}
                      onClick={() => setFormData({ ...formData, hasGbp: true })}
                      className={`flex-1 h-12 rounded-lg ${formData.hasGbp === true ? 'bg-[#0a2540] text-white' : 'border-slate-200'}`}
                      data-testid="gbp-yes-btn"
                    >
                      Yes
                    </Button>
                    <Button
                      type="button"
                      variant={formData.hasGbp === false ? "default" : "outline"}
                      onClick={() => setFormData({ ...formData, hasGbp: false })}
                      className={`flex-1 h-12 rounded-lg ${formData.hasGbp === false ? 'bg-[#0a2540] text-white' : 'border-slate-200'}`}
                      data-testid="gbp-no-btn"
                    >
                      No
                    </Button>
                  </div>
                </div>

                <Button 
                  onClick={runAudit}
                  className="w-full bg-[#d4af37] text-[#0a2540] hover:bg-[#b5952f] text-lg font-semibold rounded-full py-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 mt-4"
                  data-testid="run-audit-btn"
                >
                  Run AI Visibility Audit
                  <Search className="ml-2 w-5 h-5" />
                </Button>
              </CardContent>
            </Card>

            <p className="mt-6 text-center text-sm text-slate-500">
              Your information is secure and never shared with third parties.
            </p>
          </div>
        ) : (
          <div className="animate-fade-in-up">
            <Card className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(10,37,64,0.08)] overflow-hidden">
              <CardContent className="p-8 md:p-12">
                <div className="text-center">
                  {/* Scanning Animation */}
                  <div className="relative w-32 h-32 mx-auto mb-8">
                    <div className="absolute inset-0 rounded-full border-4 border-[#0a2540]/20" />
                    <div className="absolute inset-0 rounded-full border-4 border-[#d4af37] border-t-transparent animate-spin" />
                    <div className="absolute inset-4 rounded-full bg-[#0a2540] flex items-center justify-center">
                      <Search className="w-10 h-10 text-[#d4af37]" />
                    </div>
                    <div className="absolute inset-0 rounded-full border-4 border-[#d4af37]/30 pulse-ring" />
                  </div>

                  <h2 className="font-heading text-2xl font-semibold text-[#0a2540] mb-2">
                    Analyzing Your AI Visibility
                  </h2>
                  <p className="text-slate-600 mb-8">{loadingMessage}</p>

                  <div className="max-w-md mx-auto">
                    <Progress 
                      value={loadingProgress} 
                      className="h-2 bg-slate-100"
                      data-testid="loading-progress"
                    />
                    <p className="mt-2 text-sm text-slate-500">{Math.round(loadingProgress)}% complete</p>
                  </div>

                  <div className="mt-8 p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">
                      <span className="font-medium">Analyzing:</span>{" "}
                      <span className="font-mono text-[#0a2540]">
                        "best {formData.serviceType === "other" ? formData.customService : formData.serviceType} in {formData.location}"
                      </span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AuditPage;
